import { gql, useMutation, useQuery } from '@apollo/client';
import { CvlPaymentStatusEnumType } from '__generated__/graphql';
import { FC, useState } from 'react';

import Toast from 'primitives/Toast';
import useToast from 'primitives/Toast/useToast';

const CVL_INVESTOR_APPLICATION_PAYMENT_QUERY = gql(`
  query InvestorAccreditationApplicationForPayment($investmentEntityId: ID!) {
    investorAccreditationApplication(investmentEntityId: $investmentEntityId) {
      id
      validityPeriodOfAccreditation
      paymentStatus
    }
  }
`);

const REGENERATE_CVL_OTP_MUTATION = gql(`
  mutation ReGenerateOtp($investorAccreditationApplicationId: ID!) {
    reGenerateOtp(investorAccreditationApplicationId: $investorAccreditationApplicationId)
  }
`);

const REGENERATE_PAYMENT_LINK_MUTATION = gql(`
  mutation RegeneratePaymentLink($investorAccreditationApplicationId: ID!) {
    regeneratePaymentLink(investorAccreditationApplicationId: $investorAccreditationApplicationId) {
      id
      paymentUrl
    }
  }
`);

const SAVE_CVL_APPLICATION_MUTATION = gql(`
  mutation SaveCvlApplication($investorAccreditationApplicationId: ID!, $otp: String!) {
    saveCvlApplication(investorAccreditationApplicationId: $investorAccreditationApplicationId, otp: $otp)
  }
`);

type PaymentProps = {
  selectedInvestmentEntity: any | null;
  onProceedToSubmitStep: () => void;
  onBack?: () => void;
};

const Payment: FC<PaymentProps> = ({ selectedInvestmentEntity, onProceedToSubmitStep, onBack }) => {
  const [otp, setOtp] = useState('');

  const [regenerateCvlOtp, { loading: regeneratingOtp, error: regenerateOtpError }] = useMutation(
    REGENERATE_CVL_OTP_MUTATION
  );
  const [saveCvlApplication, { loading: savingApplication }] = useMutation(
    SAVE_CVL_APPLICATION_MUTATION
  );
  const [regeneratePaymentLink, { loading: regeneratingPaymentLink }] = useMutation(
    REGENERATE_PAYMENT_LINK_MUTATION
  );
  const {
    showToast: showOtpSuccessToast,
    handleToastOpen: handleOtpSuccessToastOpen,
    handleToastClose: handleOtpSuccessToastClose,
  } = useToast();
  const {
    showToast: showOtpErrorToast,
    handleToastOpen: handleOtpErrorToastOpen,
    handleToastClose: handleOtpErrorToastClose,
  } = useToast();
  const {
    showToast: showPaymentLinkErrorToast,
    handleToastOpen: handlePaymentLinkErrorToastOpen,
    handleToastClose: handlePaymentLinkErrorToastClose,
  } = useToast();

  const { data, loading } = useQuery(CVL_INVESTOR_APPLICATION_PAYMENT_QUERY, {
    variables: { investmentEntityId: selectedInvestmentEntity?.id },
    skip: !selectedInvestmentEntity?.id,
    fetchPolicy: 'network-only',
  });

  const [submitError, setSubmitError] = useState<string | undefined>(undefined);
  const cvlApplicationId = data?.investorAccreditationApplication?.id;
  const paymentStatus = (data?.investorAccreditationApplication?.paymentStatus ?? null) as
    | CvlPaymentStatusEnumType
    | null
    | undefined;
  const rawValidity = data?.investorAccreditationApplication?.validityPeriodOfAccreditation;
  const validityCode =
    typeof rawValidity === 'number'
      ? rawValidity
      : rawValidity != null
      ? Number(rawValidity)
      : undefined;
  const validityYears = validityCode === 1 ? 2 : validityCode === 2 ? 3 : 2;
  const applicationFee = validityYears === 3 ? '₹17,110 (incl. tax)' : '₹11,800 (incl. tax)';
  const feeBreakup =
    validityYears === 3
      ? { processingFee: '5,000/-', certificationCharges: '9,500/-', total: '14,500/-' }
      : { processingFee: '5,000/-', certificationCharges: '5,000/-', total: '10,000/-' };

  const isPaymentPending = paymentStatus === CvlPaymentStatusEnumType.Pending;
  const isPaymentSuccess = paymentStatus === CvlPaymentStatusEnumType.Success;
  const isPaymentFailed = paymentStatus === CvlPaymentStatusEnumType.Failed;

  const handlePayNow = async () => {
    if (!cvlApplicationId) {
      // eslint-disable-next-line no-console
      console.error('Cannot generate payment link without a CVL investor application ID');
      return;
    }

    // Always regenerate, since the link created at registration may have
    // expired by the time the user reaches this step (there's no guaranteed
    // window on the payment link's validity).
    try {
      const result = await regeneratePaymentLink({
        variables: { investorAccreditationApplicationId: cvlApplicationId },
      });

      const freshPaymentUrl = result.data?.regeneratePaymentLink?.paymentUrl;
      if (!freshPaymentUrl) {
        setSubmitError(result.errors?.[0]?.message);
        handlePaymentLinkErrorToastOpen();
        return;
      }

      window.location.href = freshPaymentUrl;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error while regenerating payment link for accreditation', error);
      setSubmitError((error as Error)?.message);
      handlePaymentLinkErrorToastOpen();
    }
  };

  const handleRegenerateOtp = () => {
    if (!cvlApplicationId) {
      // eslint-disable-next-line no-console
      console.error('Cannot re-generate OTP without a CVL investor application ID');
      return;
    }

    regenerateCvlOtp({
      variables: {
        investorAccreditationApplicationId: cvlApplicationId,
      },
    })
      .then(response => {
        const isSuccess = response?.data?.reGenerateOtp === true;
        if (isSuccess) {
          handleOtpSuccessToastOpen();
        } else {
          setSubmitError(response?.errors?.[0]?.message);
          handleOtpErrorToastOpen();
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error while re-generating OTP for accreditation payment', error);
        setSubmitError(error?.message);
        handleOtpErrorToastOpen();
      });
  };

  const handleSaveDraftApplication = () => {
    if (!cvlApplicationId) {
      // eslint-disable-next-line no-console
      console.error('Cannot save CVL application without a CVL investor application ID');
      return;
    }

    saveCvlApplication({
      variables: {
        investorAccreditationApplicationId: cvlApplicationId,
        otp,
      },
    })
      .then(response => {
        const isSuccess = response?.data?.saveCvlApplication === true;
        if (isSuccess) {
          onProceedToSubmitStep();
        } else {
          setSubmitError(response?.errors?.[0]?.message);
          handleOtpErrorToastOpen();
        }
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error('Error while saving CVL accreditation application', error);
        setSubmitError(error?.message);
        handleOtpErrorToastOpen();
      });
  };

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="mb-2 text-lg font-semibold">Payment</h3>
            <p className="text-sm text-gray-600">
              Please review the accreditation application fee and validity details before proceeding
              with payment.
            </p>
          </div>
          {paymentStatus && (
            <span
              className={[
                'inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold ring-1',
                isPaymentSuccess
                  ? 'bg-green-100 text-green-800 ring-green-200'
                  : isPaymentFailed
                  ? 'bg-red-100 text-red-800 ring-red-200'
                  : 'bg-yellow-100 text-yellow-800 ring-yellow-200',
              ].join(' ')}
            >
              <span
                className={[
                  'h-2 w-2 rounded-full',
                  isPaymentSuccess
                    ? 'bg-green-500'
                    : isPaymentFailed
                    ? 'bg-red-500'
                    : 'bg-yellow-500',
                ].join(' ')}
              />
              {isPaymentSuccess
                ? 'Payment Completed'
                : isPaymentFailed
                ? 'Payment Failed'
                : 'Payment Pending'}
            </span>
          )}
        </div>

        <div className="rounded-md border border-gray-200 bg-gray-50 p-4 text-sm">
          <div className="flex items-center justify-between">
            <p className="font-medium text-gray-800">Fee breakup</p>
            <span className="text-xs text-gray-500">{validityYears} Year validity</span>
          </div>
          <div className="mt-3 space-y-2 text-gray-700">
            <div className="flex items-center justify-between">
              <span>Processing Fee as charged by CVL</span>
              <span>{feeBreakup.processingFee}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Certification Charges as charged by CVL</span>
              <span>{feeBreakup.certificationCharges}</span>
            </div>
            <div className="flex items-center justify-between border-t border-gray-200 pt-2 font-medium text-gray-800">
              <span>Total</span>
              <span>{feeBreakup.total}</span>
            </div>
          </div>
          <div className="mt-3 space-y-1 border-t border-gray-200 pt-3 text-xs text-gray-600">
            <p className="font-medium text-gray-700">Note:</p>
            <p>The Processing fee would be non-refundable.</p>
            <p>18% GST is applicable on the total fees.</p>
          </div>
          <div className="mt-3 flex items-center justify-between border-t border-gray-200 pt-3">
            <span className="font-medium text-gray-800">
              Amount payable ({validityYears} years, incl. 18% GST)
            </span>
            <span className="text-base font-semibold text-gray-900">{applicationFee}</span>
          </div>
        </div>

        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <p className="font-semibold">Important</p>
          <p className="mt-1">
            Once you complete the payment, you won&apos;t be able to change any details in your
            accreditation application.
          </p>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            By clicking &quot;Pay now to proceed&quot; you agree to the terms of the accreditation
            review.
          </p>
          {!isPaymentSuccess ? (
            <div className="ml-4 flex items-center gap-3">
              {onBack ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                >
                  Previous step
                </button>
              ) : null}
              <button
                type="button"
                onClick={handlePayNow}
                disabled={loading || regeneratingPaymentLink || !cvlApplicationId}
                className={[
                  'inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2',
                  loading || regeneratingPaymentLink || !cvlApplicationId
                    ? 'cursor-not-allowed bg-gray-300 text-gray-600 focus:ring-gray-300'
                    : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-600',
                ].join(' ')}
              >
                {regeneratingPaymentLink
                  ? 'Generating payment link...'
                  : isPaymentPending || !paymentStatus
                  ? 'Pay now to proceed'
                  : isPaymentFailed
                  ? 'Retry payment'
                  : 'Pay now to proceed'}
              </button>
            </div>
          ) : (
            <div className="ml-4 flex items-end space-x-3">
              <div className="flex flex-col">
                <label
                  htmlFor="registration-otp"
                  className="mb-1 text-xs font-medium text-gray-700"
                >
                  OTP received during registration
                </label>
                <input
                  id="registration-otp"
                  type="text"
                  value={otp}
                  onChange={event => setOtp(event.target.value)}
                  placeholder="Enter OTP"
                  className="w-40 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  OTP is valid for 30 minutes. If it has expired, you can request a new one.
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Can&apos;t find it? Please check your spam/junk folder as well.
                </p>
                <button
                  type="button"
                  onClick={handleRegenerateOtp}
                  disabled={regeneratingOtp}
                  className={[
                    'mt-2 inline-flex items-center self-start rounded-md border px-3 py-1 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2',
                    regeneratingOtp
                      ? 'cursor-not-allowed border-gray-300 text-gray-400 focus:ring-gray-300'
                      : 'border-indigo-600 text-indigo-600 hover:bg-indigo-50 focus:ring-indigo-600',
                  ].join(' ')}
                >
                  {regeneratingOtp ? 'Re-generating OTP...' : 'Re-generate OTP'}
                </button>
              </div>
              <div className="flex items-center gap-3">
                {onBack ? (
                  <button
                    type="button"
                    onClick={onBack}
                    className="inline-flex items-center rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                  >
                    Previous step
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={handleSaveDraftApplication}
                  disabled={!otp || savingApplication}
                  className={[
                    'inline-flex items-center rounded-md px-4 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2',
                    !otp || savingApplication
                      ? 'cursor-not-allowed bg-gray-300 text-gray-600 focus:ring-gray-300'
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-600',
                  ].join(' ')}
                >
                  {savingApplication ? 'Saving…' : 'Save Application'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <Toast
        type="success"
        title="OTP re-generated"
        message="A new OTP has been sent to your registered mobile number."
        show={showOtpSuccessToast}
        handleClose={handleOtpSuccessToastClose}
      />
      <Toast
        type="error"
        title="Unable to re-generate OTP"
        message={
          submitError || 'We were unable to re-generate your OTP. Please try again in a moment.'
        }
        show={showOtpErrorToast}
        handleClose={handleOtpErrorToastClose}
      />
      <Toast
        type="error"
        title="Unable to generate payment link"
        message={
          submitError ||
          'We were unable to generate a fresh payment link. Please try again in a moment.'
        }
        show={showPaymentLinkErrorToast}
        handleClose={handlePaymentLinkErrorToastClose}
      />
    </>
  );
};

export default Payment;
